/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import 'swagger-ui-react/swagger-ui.css';
import './swagger.css';
import SwaggerUILib from 'swagger-ui-react';
import { OpenAPIObject } from 'openapi3-ts';
import { Api } from '../../../data/interfaces';
import { PATH_SEPARATOR } from '../../../data/constants';
import { isThirdPartyApi } from '../../../data/utils';
import { SecuritySchemeType } from './utils';
import { useIntl } from "react-intl";

const MAX_RESPONSE_SIZE = 4 * 1024 * 1024;

const disableAuthorizeAndInfoPlugin = {
    statePlugins: {
        spec: {
            wrapSelectors: {
                servers: () => () => [],
                securityDefinitions: () => () => null,
                schemes: () => () => []
            }
        }
    },
    wrapComponents: {
        info: () => () => null,
        authorizeBtn: () => () => null,
    },
};

export enum DocExpansion {
    LIST = 'list',
    FULL = 'full',
    NONE = 'none',
  }

export interface SwaggerUIProps {
    spec: OpenAPIObject,
    accessTokenProvider: string,
    securitySchemeType: string,
    authorizationHeader: string,
    api: Api,
    apiGatewayUrl: string,
    docExpansion?: DocExpansion,
}

/**
 *
 * @class SwaggerUI
 * @extends {Component}
 */
const SwaggerUI = (props: SwaggerUIProps) => {
    const {
        spec, accessTokenProvider, authorizationHeader, api, securitySchemeType, apiGatewayUrl, docExpansion
    } = props;
    const intl = useIntl();

    const responseInterceptor = (response: any) => {
      if (response.status === 200) {
        const responsePayload = response.data;
        const responseSize = new Blob([responsePayload]).size;
        if (responseSize > MAX_RESPONSE_SIZE) {
          return {
            ...response,
            text: intl.formatMessage({
              id: "swagger.response.tooLarge",
              defaultMessage: "Response is too large to render",
            }),
          };
        }
      }
      return response;
    };

    const componentProps = {
        spec,
        validatorUrl: null,
        defaultModelsExpandDepth: -1,
        docExpansion: docExpansion,
        requestInterceptor: (req: any) => {
            // Interceptor adding the protocol http (unsecured) even the request URL contains the protocol (only with swagger 2).
            // Hense entire protocol part replace with an empty string
            req.url = req.url.replace('http://', '');
            const { context, version } = api;
            if (isThirdPartyApi(api)) {
                const newUrl = apiGatewayUrl + req.url.substr(req.url.indexOf(api.context) + api.context.length);
                req.url = newUrl.replace(context, '');
            }
            const patternToCheck = `${apiGatewayUrl}/*`;
            if (isThirdPartyApi(api)) {
                if (authorizationHeader !== '') {
                    req.headers[authorizationHeader] = accessTokenProvider;
                }
            } else if (authorizationHeader === 'test-key') {
                const headerName = 'Test-key';
                req.headers[headerName] = accessTokenProvider;
            } else if (securitySchemeType === SecuritySchemeType.BASIC) {
                req.headers[authorizationHeader] = 'Basic ' + accessTokenProvider;
            } else if (securitySchemeType === SecuritySchemeType.TEST) {
                req.headers[authorizationHeader] = accessTokenProvider;
            } else if (securitySchemeType === SecuritySchemeType.API_KEY) {
                req.headers[authorizationHeader] = accessTokenProvider;
            } else {
                req.headers[authorizationHeader] = 'Bearer ' + accessTokenProvider;
            }
            if (req.url.endsWith(patternToCheck)) {
                req.url = req.url.substring(0, req.url.length - 2);
            } else if (req.url.includes(patternToCheck + '?')) { // Check for query parameters.
                const splitTokens = req.url.split('/*?');
                req.url = splitTokens.length > 1 ? splitTokens[0] + '?' + splitTokens[1] : splitTokens[0];
            }
            return req;
        },
        defaultModelExpandDepth: -1,
        plugins: [disableAuthorizeAndInfoPlugin],
        responseInterceptor,
    };
    return <SwaggerUILib {...componentProps} />;
};

export default SwaggerUI;
